import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient } from '../../api/client';
import { ExpectedApiError } from './errors';
import { buildListQueryParams, withHiddenFilter } from './query';

export async function getContacts(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
  textQuery?: string,
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: withHiddenFilter(query),
    page, limit, range, now,
    rangeName: 'date',
    sortingField: sortingField ?? 'date',
    sortDirection: sortDirection ?? 'desc',
    textQuery,
  });
  const { data, error } = await client.GET('/contacts', { params: { query: params } });
  if (error) throw new Error(`Failed to fetch contacts: ${JSON.stringify(error)}`);
  return data;
}

export async function getContactMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/contacts/meta');
  if (error) throw new Error(`Failed to fetch contacts meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getContact(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/contacts/{uuid}', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to fetch contact: ${JSON.stringify(error)}`);
  return data;
}

export async function hideContact(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/contacts/{uuid}/hide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to hide contact: ${JSON.stringify(error)}`);
}

export async function unhideContact(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/contacts/{uuid}/unhide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to unhide contact: ${JSON.stringify(error)}`);
}

export async function hideContacts(uuids: string[]) {
  for (const uuid of uuids) await hideContact(uuid);
}

export async function unhideContacts(uuids: string[]) {
  for (const uuid of uuids) await unhideContact(uuid);
}

export async function createSMSSession(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.POST('/contacts/{uuid}/sms', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to create SMS session: ${JSON.stringify(error)}`);
  return data;
}

export async function getSMSSession(uuid: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/contacts/{uuid}/sms', { params: { path: { uuid } } });
  if (error && response.status === 404) throw new ExpectedApiError('SMS session not found (404)');
  if (error) throw new Error(`Failed to fetch SMS session: ${JSON.stringify(error)}`);
  return data;
}
